import type { LayerState } from '@vitals/shared';
import type { VitalDef, VitalTone } from './vitals.js';

const WARN_THRESHOLD = 1000;

/** Map customer-out count to a three-level tone. */
export function powerTone(customersOut: number): VitalTone {
  if (customersOut === 0) return 'good';
  if (customersOut < WARN_THRESHOLD) return 'warn';
  return 'bad';
}

/**
 * Vital selector for the ConEd power outages layer.
 *
 * Display: value = total customers out, label "power".
 * 0 out → good tone. 1–999 → warn. ≥1000 → bad.
 */
export const powerOutagesVital: VitalDef = {
  id: 'power',
  layerId: 'power_outages',

  select(s: LayerState | undefined) {
    const isDown = !s || s.status === 'down' || s.summary == null;
    if (isDown) {
      return {
        value: '—',
        label: 'power',
        down: true,
        stale: false,
      };
    }

    const stale = s.status === 'stale';
    const { totalCustomersOut } = s.summary as {
      totalCustomersOut: number;
      totalOutages: number;
    };

    if (totalCustomersOut === 0) {
      return {
        value: '0',
        label: 'power',
        down: false,
        stale,
        tone: 'good' as VitalTone,
      };
    }

    return {
      value: `${totalCustomersOut.toLocaleString('en-US')} out`,
      label: 'power',
      down: false,
      stale,
      tone: powerTone(totalCustomersOut),
    };
  },
};
